import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, CalendarDays, ChevronDown, ExternalLink, Eye, List, Loader2, PlayCircle } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { API_BASE, MAX_CHANNEL_LINK } from "@/config/links";
import { trackClick } from "@/lib/track";
import { recordView } from "@/lib/views";
import { useSeo, SITE_URL } from "@/lib/seo";

function formatViews(value) {
  return Number(value || 0).toLocaleString("ru-RU");
}

function formatDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function shortDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
  });
}

function monthKey(value) {
  return /^\d{4}-\d{2}/.test(value || "") ? value.slice(0, 7) : "unknown";
}

function monthLabel(key) {
  if (key === "unknown") return "Без даты";
  const [year, month] = key.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("ru-RU", {
    month: "long",
    year: "numeric",
  });
}

// fetch при недоступном API кидает TypeError "Failed to fetch" — латиницу пользователю не показываем.
function friendlyError(error, fallback) {
  const message = error?.message || "";
  return /[А-Яа-яЁё]/.test(message) ? message : fallback;
}

function cleanLine(line) {
  return line.replace(/^#+\s*/, "").replace(/^["'«]+|["'»]+$/g, "").trim();
}

function storyParagraphs(story) {
  if (!story?.text) return [];
  const lines = story.text.split(/\r?\n/).map(cleanLine).filter(Boolean);
  if (lines[0] === story.title) return lines.slice(1);
  return lines;
}

function groupStoriesByMonth(stories) {
  const groups = new Map();
  for (const story of stories) {
    const key = monthKey(story.date);
    if (!groups.has(key)) groups.set(key, { key, label: monthLabel(key), items: [] });
    groups.get(key).items.push(story);
  }
  return [...groups.values()];
}

const StoryPage = () => {
  const { sourceKey } = useParams();
  const [state, setState] = useState({ loading: true, error: "", story: null, stories: [] });

  useEffect(() => {
    let alive = true;
    async function loadStory() {
      try {
        const storyResponse = await fetch(`${API_BASE}/api/stories/${encodeURIComponent(sourceKey)}`);
        if (storyResponse.status === 404) throw new Error("Эта сказка не найдена");
        if (!storyResponse.ok) throw new Error("Не удалось загрузить сказку");
        const storyData = await storyResponse.json();

        let stories = [];
        try {
          const archiveResponse = await fetch(`${API_BASE}/api/stories?limit=500`);
          if (archiveResponse.ok) {
            const archiveData = await archiveResponse.json();
            stories = archiveData.stories || [];
          }
        } catch {
          stories = [];
        }

        if (alive) {
          setState({
            loading: false,
            error: "",
            story: storyData.story || null,
            stories,
          });
        }
      } catch (error) {
        if (alive) {
          setState({
            loading: false,
            error: friendlyError(error, "Сказка временно недоступна"),
            story: null,
            stories: [],
          });
        }
      }
    }
    loadStory();
    return () => {
      alive = false;
    };
  }, [sourceKey]);

  useEffect(() => {
    if (state.story?.hasVideo) recordView("stories", sourceKey);
  }, [state.story?.hasVideo, sourceKey]);

  const story = state.story;
  const storyPath = story?.slug ? `/stories/${story.sourceKey}/${story.slug}` : `/stories/${sourceKey}`;
  useSeo({
    title: story ? `${story.title} — сказка на ночь | Сказки детям на ночь` : undefined,
    description: story?.excerpt,
    path: storyPath,
    image: story?.coverUrl || undefined,
    jsonLd: story
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: story.title,
          description: story.excerpt,
          image: story.coverUrl || undefined,
          datePublished: story.publishedAt || story.createdAt || undefined,
          url: `${SITE_URL}${storyPath}`,
          inLanguage: "ru",
          isFamilyFriendly: true,
          author: { "@type": "Organization", name: "Сказки детям на ночь" },
          publisher: { "@type": "Organization", name: "Сказки детям на ночь" },
        }
      : undefined,
  });

  const paragraphs = useMemo(() => storyParagraphs(state.story), [state.story]);
  const archiveStories = useMemo(() => {
    if (!state.story) return state.stories;
    const exists = state.stories.some((story) => story.sourceKey === state.story.sourceKey);
    return exists ? state.stories : [state.story, ...state.stories];
  }, [state.story, state.stories]);
  const storyGroups = useMemo(() => groupStoriesByMonth(archiveStories), [archiveStories]);
  const activeGroupKey = monthKey(state.story?.date);

  return (
    <div className="min-h-screen bg-night-900 text-cream">
      <Header />
      <main>
        {state.loading ? (
          <section className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Загружаю сказку
          </section>
        ) : state.error || !state.story ? (
          <section className="py-14">
            <div className="mx-auto max-w-3xl px-4 sm:px-6">
              <Link to="/stories" className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-cream transition-colors">
                <ArrowLeft className="h-4 w-4" />
                Все сказки
              </Link>
              <div className="rounded-2xl border border-amber/30 bg-amber/10 px-5 py-5 text-amber-soft">{state.error || "Сказка не найдена"}</div>
            </div>
          </section>
        ) : (
          <>
            <section className="border-b border-line">
              <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 md:py-10">
                <div className="mb-6 flex flex-wrap items-center gap-3">
                  <Link to="/stories" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-cream transition-colors">
                    <ArrowLeft className="h-4 w-4" />
                    Все сказки
                  </Link>
                  <span className="text-line-strong">/</span>
                  <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <CalendarDays className="h-4 w-4" />
                    {formatDate(state.story.date)}
                  </span>
                  {state.story.hasVideo ? (
                    <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                      <Eye className="h-4 w-4" />
                      {formatViews(state.story.views)}
                    </span>
                  ) : null}
                </div>

                <div className="max-w-3xl">
                  {state.story.rubric ? (
                    <span className="mb-3 inline-flex items-center rounded-full bg-amber/15 border border-amber/25 text-amber-soft px-2.5 py-0.5 text-[11px] font-medium">
                      {state.story.rubric}
                    </span>
                  ) : null}
                  <h1 className="font-serif text-4xl leading-tight text-cream sm:text-5xl">{state.story.title}</h1>
                  {state.story.theme ? <p className="mt-4 text-base leading-7 text-muted-foreground">{state.story.theme}</p> : null}
                </div>
              </div>
            </section>

            <section className="py-8 md:py-10">
              <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[300px_minmax(0,1fr)]">
                <aside className="order-2 lg:order-1 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto">
                  <nav className="rounded-2xl border border-line bg-night-card p-4" aria-label="Другие сказки">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 text-sm font-semibold text-cream">
                          <List className="h-4 w-4" />
                          Другие сказки
                        </div>
                        <div className="mt-1 text-xs text-dim">{archiveStories.length} сказок</div>
                      </div>
                      <Link to="/stories" className="rounded-full border border-line-strong px-2 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-amber hover:text-cream">
                        Все сказки
                      </Link>
                    </div>

                    <div className="mb-5 grid grid-cols-2 gap-2 border-b border-line pb-4">
                      <a href="#story-video" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-line-strong bg-night-900 px-3 text-sm font-medium text-muted-foreground transition-colors hover:border-amber hover:text-cream">
                        <PlayCircle className="h-4 w-4" />
                        Видео
                      </a>
                      <a href="#story-text" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-line-strong bg-night-900 px-3 text-sm font-medium text-muted-foreground transition-colors hover:border-amber hover:text-cream">
                        <BookOpen className="h-4 w-4" />
                        Текст
                      </a>
                    </div>

                    <div className="space-y-2">
                      {storyGroups.map((group) => (
                        <details key={group.key} className="group" open={group.key === activeGroupKey || storyGroups.length === 1}>
                          <summary className="flex min-h-10 cursor-pointer list-none items-center justify-between gap-3 rounded-lg px-2 text-sm font-semibold text-cream transition-colors hover:bg-night-700 [&::-webkit-details-marker]:hidden">
                            <span className="capitalize">{group.label}</span>
                            <span className="inline-flex items-center gap-2 text-xs font-medium text-dim">
                              {group.items.length}
                              <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                            </span>
                          </summary>
                          <div className="mt-1 space-y-1 border-l border-line pl-3">
                            {group.items.map((story) => {
                              const active = story.sourceKey === sourceKey;
                              return (
                                <Link
                                  key={story.sourceKey}
                                  to={story.slug ? `/stories/${story.sourceKey}/${story.slug}` : `/stories/${story.sourceKey}`}
                                  className={`block rounded-lg px-3 py-2 text-sm leading-5 transition-colors ${
                                    active
                                      ? "bg-amber/15 font-semibold text-amber-soft"
                                      : "text-muted-foreground hover:bg-night-700 hover:text-cream"
                                  }`}
                                >
                                  <span className="mb-1 block text-xs font-medium text-dim">{shortDate(story.date)}</span>
                                  <span>{story.title}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </details>
                      ))}
                    </div>
                  </nav>
                </aside>

                <div className="order-1 lg:order-2 min-w-0">
                  <section id="story-video" className="scroll-mt-24">
                    {state.story.videoUrl ? (
                      <div className="overflow-hidden rounded-2xl border border-line-strong bg-black shadow-[0_16px_48px_-16px_rgba(0,0,0,0.7)]">
                        <video
                          className="aspect-video w-full bg-black"
                          src={state.story.videoUrl}
                          poster={state.story.coverUrl || undefined}
                          controls
                          preload="metadata"
                        />
                      </div>
                    ) : state.story.coverUrl ? (
                      <div className="overflow-hidden rounded-2xl border border-line-strong bg-black">
                        <img src={state.story.coverUrl} alt={state.story.title} className="w-full aspect-video object-cover" />
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber/25 bg-amber/10 px-5 py-4">
                        <div className="flex items-center gap-2 font-semibold text-amber-soft">
                          <PlayCircle className="h-5 w-5" />
                          Видео этой сказки — в MAX-канале
                        </div>
                        <a
                          href={state.story.postUrl || MAX_CHANNEL_LINK}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackClick("channel")}
                          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-amber hover:bg-amber-hover px-4 text-sm font-semibold text-night-900 transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Смотреть в канале
                        </a>
                      </div>
                    )}
                  </section>

                  <article id="story-text" className="scroll-mt-24 pt-10">
                    <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-cream">
                      <BookOpen className="h-4 w-4" />
                      Текст сказки
                    </div>
                    <div className="reading-column">
                      {paragraphs.map((paragraph, index) => (
                        <p key={`${state.story.sourceKey}-${index}`}>{paragraph}</p>
                      ))}
                    </div>
                  </article>

                  <div className="mt-10 flex flex-wrap gap-3 border-t border-line pt-6">
                    {state.story.postUrl ? (
                      <a
                        href={state.story.postUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-amber hover:bg-amber-hover px-5 text-sm font-semibold text-night-900 transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Смотреть в канале
                      </a>
                    ) : null}
                    <Link
                      to="/stories"
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-line-strong px-5 text-sm font-semibold text-muted-foreground transition-colors hover:border-amber hover:text-cream"
                    >
                      <BookOpen className="h-4 w-4" />
                      Все сказки
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default StoryPage;
